// Regras puras do vínculo do agente (conexão + filas + "antes da
// distribuição") no modal do Talk.ai. Espelham as validações do backend
// (SalvaVinculosDoAgente) e o helper ResolveAgentForTicket, para o modal
// mostrar o conflito antes de o servidor recusar.

const mesmoId = (a, b) =>
  a !== null && a !== undefined && a !== "" && Number(a) === Number(b);

const idsNumericos = (queueIds) =>
  (Array.isArray(queueIds) ? queueIds : []).map(Number);

const conexaoPorId = (whatsapps, whatsappId) =>
  (whatsapps || []).find((w) => mesmoId(whatsappId, w.id));

export const filasDaConexao = (whatsapp) =>
  Array.isArray(whatsapp?.queues) ? whatsapp.queues : [];

/** Filas marcadas que pertencem à conexão — trocar a conexão descarta o resto. */
export const limpaFilasInvalidas = (queueIds, whatsapp) => {
  const validas = new Set(filasDaConexao(whatsapp).map((q) => Number(q.id)));
  return idsNumericos(queueIds).filter((id) => validas.has(id));
};

/** Filas marcadas que saíram da conexão depois do vínculo (vínculo órfão). */
export const filasForaDaConexao = (queueIds, whatsapp) => {
  const validas = new Set(filasDaConexao(whatsapp).map((q) => Number(q.id)));
  return idsNumericos(queueIds).filter((id) => !validas.has(id));
};

/**
 * { [queueId]: nome do agente } das filas que outro agente já ocupa na
 * conexão. Uma (conexão, fila) tem no máximo um agente; o agente em edição
 * não conta, e o legado sem conexão não ocupa fila de conexão nenhuma.
 */
export const ocupacaoDasFilas = ({ prompts, whatsappId, promptIdAtual }) => {
  const mapa = {};
  if (whatsappId === null || whatsappId === undefined || whatsappId === "") return mapa;
  (prompts || []).forEach((p) => {
    if (!mesmoId(p.whatsappId, whatsappId)) return;
    if (mesmoId(promptIdAtual, p.id)) return;
    idsNumericos(p.queueIds).forEach((queueId) => {
      mapa[queueId] = p.name;
    });
  });
  return mapa;
};

/**
 * Nome do outro agente que já atende antes da distribuição na conexão
 * (`Whatsapps.promptId`), ou null quando a vaga está livre.
 */
export const antesDaFilaOcupadoPor = ({
  whatsapps,
  prompts,
  whatsappId,
  promptIdAtual,
}) => {
  const conexao = conexaoPorId(whatsapps, whatsappId);
  if (!conexao || !conexao.promptId) return null;
  if (mesmoId(promptIdAtual, conexao.promptId)) return null;
  const agente = (prompts || []).find((p) => mesmoId(conexao.promptId, p.id));
  return agente?.name || conexao.prompt?.name || String(conexao.promptId);
};

/**
 * Outras conexões em que o agente ainda atende antes da distribuição (só
 * acontece com dado legado). Ao salvar, o backend limpa todas elas — spec §13.5.
 */
export const conexoesComAntesDaFilaExtra = ({ whatsapps, promptId, whatsappId }) => {
  if (!promptId) return [];
  return (whatsapps || []).filter(
    (w) => mesmoId(promptId, w.promptId) && !mesmoId(whatsappId, w.id)
  );
};

const filasEscolhidas = (queueIds, whatsapp) => {
  const ids = new Set(idsNumericos(queueIds));
  return filasDaConexao(whatsapp).filter((q) => ids.has(Number(q.id)));
};

const temChatbot = (fila) =>
  Number(fila?.chatbotCount) > 0 ||
  (Array.isArray(fila?.chatbots) && fila.chatbots.length > 0);

/** Filas escolhidas com integração (n8n etc.): a integração atende antes do agente. */
export const filasComIntegracao = (queueIds, whatsapp) =>
  filasEscolhidas(queueIds, whatsapp).filter((q) => !!q.integrationId);

/** Filas escolhidas com opções de chatbot: o chatbot vence e o agente não responde. */
export const filasComChatbot = (queueIds, whatsapp) =>
  filasEscolhidas(queueIds, whatsapp).filter(temChatbot);

export const conexaoTemIntegracao = (whatsapp) => !!whatsapp?.integrationId;

/** Espelha temMenuNaConexao do backend: 2+ filas, ou 1 fila com chatbot. */
export const conexaoTemMenu = (whatsapp) => {
  const filas = filasDaConexao(whatsapp);
  return filas.length >= 2 || (filas.length === 1 && temChatbot(filas[0]));
};

/**
 * As filas que vêm no WhatsAppsContext só têm id/nome/cor; integração e
 * contagem de chatbot vêm de GET /queue. Junta os dois por id.
 */
export const completaFilasDasConexoes = (whatsapps, filasDaEmpresa) => {
  const porId = new Map((filasDaEmpresa || []).map((q) => [Number(q.id), q]));
  return (whatsapps || []).map((w) => ({
    ...w,
    queues: filasDaConexao(w).map((q) => {
      const completa = porId.get(Number(q.id));
      return completa
        ? {
            ...q,
            integrationId: completa.integrationId ?? null,
            chatbotCount: completa.chatbotCount ?? 0,
          }
        : q;
    }),
  }));
};

/** Código de erro (o mesmo do backend) que impede salvar, ou null. */
export const erroDoVinculo = ({ whatsappId, queueIds, atendeAntesDaFila }) => {
  if (whatsappId === null || whatsappId === undefined || whatsappId === "") {
    return "ERR_PROMPT_WHATSAPP_REQUIRED";
  }
  if (idsNumericos(queueIds).length === 0 && !atendeAntesDaFila) {
    return "ERR_PROMPT_QUEUE_OR_PREQUEUE_REQUIRED";
  }
  return null;
};

export const podeSalvar = (vinculo) => erroDoVinculo(vinculo) === null;
